import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import './App.css';
import { Toaster } from './components/ui/toaster';
import { AuthProvider } from './context/AuthContext';
import { LanguageProvider } from './context/LanguageContext';

// Public pages
import HomePage from './pages/HomePage';
import EditorialHomePage from './pages/EditorialHomePage';
import InternationalPage from './pages/InternationalPage';
import BlogPostPage from './pages/BlogPostPage';
import BlogPage from './pages/BlogPage';
import ServicosPage from './pages/ServicosPage';
import ServicesPage from './pages/ServicesPage';
import PrivacyPage from './pages/PrivacyPage';
import ContactoPage from './pages/ContactoPage';
import SobrePage from './pages/SobrePage';
import RegioesPage from './pages/RegioesPage';
import PrivatePageView from './pages/PrivatePageView';

// Admin pages
import AdminLogin from './pages/admin/Login';
import AdminDashboard from './pages/admin/Dashboard';
import BlogManagement from './pages/admin/BlogManagement';
import ThemeCustomization from './pages/admin/ThemeCustomization';
import UserManagement from './pages/admin/UserManagement';
import CRMManagement from './pages/admin/CRMManagement';
import DecorativeManagement from './pages/admin/DecorativeManagement';
import SEOManagement from './pages/admin/SEOManagement';
import EmailMarketing from './pages/admin/EmailMarketing';
import PageContentManagement from './pages/admin/PageContentManagement';
import PrivatePagesManagement from './pages/admin/PrivatePagesManagement';
import SnapshotAdmin from './pages/admin/SnapshotAdmin';

import CookieConsent from './components/CookieConsent';
import ScrollToTop from './components/ScrollToTop';

/**
 * URL strategy (Onda A — May 2026):
 *   English (default): /, /advisory, /services, /regions, /international, /journal, /journal/:id, /contact
 *   Portuguese:        /pt, /pt/advisory, /pt/services, /pt/regions, /pt/international, /pt/journal, /pt/journal/:id, /pt/contact
 *
 * Both languages share the same page components — content adapts via useLanguage()
 * which is derived from the URL pathname.
 *
 * Legacy PT slugs (/sobre, /servicos, /regioes, /blog, /contacto) redirect to the new
 * /pt/* equivalents to preserve existing inbound SEO links.
 */
function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <LanguageProvider>
          <ScrollToTop />
          <div className="App">
            <Routes>
              {/* Admin (no language prefix) */}
              <Route path="/admin/login" element={<AdminLogin />} />
              <Route path="/admin/dashboard" element={<AdminDashboard />} />
              <Route path="/admin/blog" element={<BlogManagement />} />
              <Route path="/admin/theme" element={<ThemeCustomization />} />
              <Route path="/admin/users" element={<UserManagement />} />
              <Route path="/admin/crm" element={<CRMManagement />} />
              <Route path="/admin/decorative" element={<DecorativeManagement />} />
              <Route path="/admin/seo" element={<SEOManagement />} />
              <Route path="/admin/email" element={<EmailMarketing />} />
              <Route path="/admin/content" element={<PageContentManagement />} />
              <Route path="/admin/private-pages" element={<PrivatePagesManagement />} />
              <Route path="/admin/snapshot" element={<SnapshotAdmin />} />

              {/* Off-market private pages (no language prefix; noindex) */}
              <Route path="/p/:slug" element={<PrivatePageView />} />

              {/* English (default) */}
              <Route path="/" element={<EditorialHomePage />} />
              <Route path="/advisory" element={<SobrePage />} />
              <Route path="/services" element={<ServicesPage />} />
              <Route path="/regions" element={<RegioesPage />} />
              <Route path="/international" element={<InternationalPage />} />
              <Route path="/journal" element={<BlogPage />} />
              <Route path="/journal/:id" element={<BlogPostPage />} />
              <Route path="/contact" element={<ContactoPage />} />
              <Route path="/privacy" element={<PrivacyPage />} />

              {/* Portuguese mirror */}
              <Route path="/pt" element={<EditorialHomePage />} />
              <Route path="/pt/advisory" element={<SobrePage />} />
              <Route path="/pt/services" element={<ServicesPage />} />
              <Route path="/pt/regions" element={<RegioesPage />} />
              <Route path="/pt/international" element={<InternationalPage />} />
              <Route path="/pt/journal" element={<BlogPage />} />
              <Route path="/pt/journal/:id" element={<BlogPostPage />} />
              <Route path="/pt/contact" element={<ContactoPage />} />
              <Route path="/pt/privacy" element={<PrivacyPage />} />

              {/* Legacy PT slug redirects (preserve inbound SEO) */}
              <Route path="/sobre" element={<Navigate to="/pt/advisory" replace />} />
              <Route path="/servicos" element={<Navigate to="/pt/services" replace />} />
              <Route path="/regioes" element={<Navigate to="/pt/regions" replace />} />
              <Route path="/contacto" element={<Navigate to="/pt/contact" replace />} />
              <Route path="/blog" element={<Navigate to="/pt/journal" replace />} />
              <Route path="/blog/:id" element={<LegacyBlogRedirect />} />

              {/* Legacy WordPress URL fallbacks (Google still has these indexed) */}
              <Route path="/oportunidades" element={<Navigate to="/" replace />} />
              <Route path="/oportunidades/*" element={<Navigate to="/" replace />} />
              <Route path="/west-cliffs" element={<Navigate to="/regions" replace />} />
              <Route path="/west-cliffs/*" element={<Navigate to="/regions" replace />} />
              <Route path="/project/prata-riverside" element={<Navigate to="/journal" replace />} />
              <Route path="/project/prata-riverside/*" element={<Navigate to="/journal" replace />} />
              <Route path="/project/quinta-de-faro" element={<Navigate to="/journal" replace />} />
              <Route path="/project/quinta-de-faro/*" element={<Navigate to="/journal" replace />} />
              <Route path="/project/*" element={<Navigate to="/regions" replace />} />
              <Route path="/category/golden-visa" element={<Navigate to="/international" replace />} />
              <Route path="/category/golden-visa/*" element={<Navigate to="/international" replace />} />
              <Route path="/category/*" element={<Navigate to="/journal" replace />} />
              <Route path="/author/*" element={<Navigate to="/journal" replace />} />
              <Route path="/en-gb" element={<Navigate to="/" replace />} />
              <Route path="/en-gb/*" element={<Navigate to="/" replace />} />
              <Route path="/wp-content/*" element={<Navigate to="/" replace />} />
              <Route path="/wp-admin/*" element={<Navigate to="/" replace />} />

              {/* Fallback to old design (for rollback safety) */}
              <Route path="/classic" element={<HomePage />} />
            </Routes>
            <Toaster />
            <CookieConsent />
          </div>
        </LanguageProvider>
      </BrowserRouter>
    </AuthProvider>
  );
}

// Tiny helper so /blog/:id keeps its slug param when redirecting to /pt/journal/:id
const LegacyBlogRedirect = () => {
  const slug = window.location.pathname.replace(/^\/blog\//, '');
  return <Navigate to={`/pt/journal/${slug}`} replace />;
};

export default App;
